// The backend's typed API, and hooks that add the session token to every call.
import { useCallback, useMemo } from "react";
import { useAction, useMutation, usePaginatedQuery, useQuery, type OptionalRestArgsOrSkip, type PaginatedQueryArgs, type PaginatedQueryReference } from "convex/react";
import type { FunctionArgs, FunctionReference } from "convex/server";
import type { OptimisticUpdate } from "convex/browser";
import { api } from "../../../backend/convex/_generated/api";
import { getToken, useToken } from "./session";
import { useAwake } from "./awake";

export { api };

type WithToken<F extends FunctionReference<"query" | "mutation" | "action">> = Omit<FunctionArgs<F>, "token">;

// The public boundary checks every argument; this one assertion restores the omitted field.
const authenticated = <F extends FunctionReference<"query" | "mutation" | "action">>(args: WithToken<F>): FunctionArgs<F> =>
  ({ ...args, token: getToken() ?? "" }) as FunctionArgs<F>;

/** useQuery with the token added; pass "skip" to pause. */
export function useOwnerQuery<F extends FunctionReference<"query">>(fn: F, args: WithToken<F> | "skip") {
  const token = useToken();
  const awake = useAwake(); // out of sight for a while: no live queries
  const query = args === "skip" || !token || !awake ? "skip" : authenticated<F>(args);
  return useQuery(fn, ...([query] as OptionalRestArgsOrSkip<F>));
}

export function useOwnerMutation<F extends FunctionReference<"mutation">>(fn: F, optimistic?: OptimisticUpdate<FunctionArgs<F>>) {
  const m = useMutation(fn);
  const call = useMemo(() => optimistic ? m.withOptimisticUpdate(optimistic) : m, [m, optimistic]);
  return useCallback((args: WithToken<F>) => call(...([authenticated<F>(args)] as Parameters<typeof call>)), [call]);
}

export function useOwnerAction<F extends FunctionReference<"action">>(fn: F) {
  const a = useAction(fn);
  return useCallback((args: WithToken<F>) => a(...([authenticated<F>(args)] as Parameters<typeof a>)), [a]);
}

export function useOwnerPages<F extends PaginatedQueryReference>(fn: F, args: Omit<PaginatedQueryArgs<F>, "token"> | "skip", initialNumItems = 50) {
  const token = useToken();
  const awake = useAwake();
  return usePaginatedQuery(fn, args === "skip" || !token || !awake ? "skip" : { ...args, token } as unknown as PaginatedQueryArgs<F>, { initialNumItems });
}
